import type { FC } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PageTitle } from "../../components/PageTitle";
import { emailToNameMap } from "../../constants/email-senders";
import {
  type UpdateDeliveryInput,
  useDeliveryApi,
  useSendDeliveryApi,
  useUpdateDeliveryApi,
} from "../../hooks/use-delivery-api";
import { DeliveryTiming } from "../../hooks/useEmailTemplatesRestApi";
import {
  DeliveryForm,
  type DeliveryFormValues,
} from "./components/DeliveryForm";

export const DeliveryEditPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data: delivery,
    loading: deliveryLoading,
    error: deliveryError,
  } = useDeliveryApi(id || "");
  const { updateDelivery, loading: updateLoading } = useUpdateDeliveryApi();
  const { sendDelivery, loading: sendLoading } = useSendDeliveryApi();

  if (deliveryLoading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-center py-12">
          <span className="loading loading-spinner loading-lg" />
        </div>
      </div>
    );
  }

  if (deliveryError || !delivery) {
    return (
      <div className="container mx-auto px-4 py-6">
        <Link to="/deliveries" className="btn btn-ghost btn-sm mb-4">
          &larr; 戻る
        </Link>
        <div className="alert alert-error">
          <span>{deliveryError?.message || "配信メールが見つかりません"}</span>
        </div>
      </div>
    );
  }

  if (delivery.status !== "draft" && delivery.status !== "scheduled") {
    return (
      <div className="container mx-auto px-4 py-6">
        <Link to="/deliveries" className="btn btn-ghost btn-sm mb-4">
          &larr; 戻る
        </Link>
        <div className="alert alert-warning">
          <span>この配信は編集できません（ステータス: {delivery.status}）</span>
        </div>
      </div>
    );
  }

  let scheduledAt = "";
  if (delivery.scheduledAt) {
    const dt = new Date(delivery.scheduledAt);
    const local = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000);
    scheduledAt = local.toISOString().slice(0, 16);
  }

  const initialValues: DeliveryFormValues = {
    distributionGroupId: delivery.groupId || "",
    includeSender: delivery.includeSender,
    from: delivery.fromEmail,
    subject: delivery.subject,
    body: delivery.bodyText,
    deliveryTiming: delivery.scheduledAt
      ? DeliveryTiming.Scheduled
      : DeliveryTiming.Now,
    scheduledAt,
  };

  const buildUpdateInput = (
    values: DeliveryFormValues,
  ): UpdateDeliveryInput => ({
    groupId: values.distributionGroupId,
    fromEmail: values.from,
    fromName: emailToNameMap[values.from] || undefined,
    subject: values.subject.trim(),
    bodyText: values.body.trim(),
    includeSender: values.includeSender,
    scheduledAt:
      values.deliveryTiming === DeliveryTiming.Scheduled
        ? new Date(values.scheduledAt).toISOString()
        : null,
  });

  const handleSave = async (values: DeliveryFormValues) => {
    if (!id) return;
    await updateDelivery(id, buildUpdateInput(values));
    navigate("/deliveries");
  };

  const handleSend = async (values: DeliveryFormValues) => {
    if (!id) return;
    await updateDelivery(id, buildUpdateInput(values));
    if (values.deliveryTiming === DeliveryTiming.Now) {
      await sendDelivery(id);
    }
    navigate("/deliveries");
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-3xl">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/deliveries" className="btn btn-ghost btn-sm">
          &larr; 戻る
        </Link>
        <PageTitle>配信メール編集</PageTitle>
      </div>

      <DeliveryForm
        initialValues={initialValues}
        saveDraftLabel="保存"
        saveLoading={updateLoading}
        sendLoading={sendLoading}
        onSaveDraft={handleSave}
        onSend={handleSend}
      />
    </div>
  );
};
